import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Atom, Plus, PencilSimple, Trash, UploadSimple, ShareNetwork, X, Cube, Square, VideoCamera } from "@phosphor-icons/react";
import AtomARScene from "@/components/AtomARScene";
import AtomPlanet360 from "@/components/AtomPlanet360";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};

const EMPTY = { title: "", description: "", image_url: "", sort: 0 };

export default function AtomEcosystem() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | "create" | { mode:"edit", item }
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null); // { image_url, title }
  const [viewerMode, setViewerMode] = useState("3d"); // "3d" | "2d"
  const [arMode, setArMode] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/atom-ecosystem")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Error al cargar el ecosistema"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setDialog("create"); };
  const openEdit = (item) => { setForm({ title: item.title, description: item.description || "", image_url: item.image_url, sort: item.sort || 0 }); setDialog({ mode: "edit", item }); };
  const close = () => { setDialog(null); setForm(EMPTY); };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Máximo 8MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/atom-ecosystem/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Título obligatorio"); return; }
    if (!form.image_url.trim()) { toast.error("Imagen obligatoria"); return; }
    setSaving(true);
    try {
      if (dialog === "create") await api.post("/admin/atom-ecosystem", form);
      else await api.put(`/admin/atom-ecosystem/${dialog.item.id}`, form);
      toast.success(dialog === "create" ? "Elemento creado" : "Elemento actualizado");
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/atom-ecosystem/${item.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const share = async (item) => {
    const url = `${window.location.origin}/dashboard/atomo`;
    const text = `${item.title} · Materializar el Átomo · Disfruta y Diviértete`;
    try {
      if (navigator.share) await navigator.share({ title: item.title, text, url });
      else { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }
    } catch { /* dismissed */ }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="atom-ecosystem-page">
      {/* Hero orbital header */}
      <div className="relative overflow-hidden rounded-lg border border-[#CCFF00]/30 bg-gradient-to-br from-[#050510] via-[#0a0a20] to-black p-6 md:p-10" data-testid="atom-hero">
        {/* Cosmic backdrop */}
        <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
          <div className="absolute top-4 left-8 w-1 h-1 rounded-full bg-white animate-pulse" />
          <div className="absolute top-16 right-24 w-1.5 h-1.5 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.6s" }} />
          <div className="absolute bottom-8 left-1/3 w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "1.2s" }} />
          <div className="absolute top-1/2 right-8 w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "1.8s" }} />
          <div className="absolute bottom-16 right-1/4 w-1.5 h-1.5 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.3s" }} />
        </div>

        <div className="relative flex items-start gap-6 flex-wrap">
          {/* Animated atom / orbits */}
          <div className="relative w-32 h-32 md:w-40 md:h-40 shrink-0" aria-hidden>
            <div className="absolute inset-0 rounded-full border border-[#CCFF00]/30" style={{ animation: "atom-spin 12s linear infinite" }}>
              <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#CCFF00] shadow-[0_0_12px_rgba(204,255,0,0.9)]" />
            </div>
            <div className="absolute inset-3 rounded-full border border-orange-400/40 rotate-45" style={{ animation: "atom-spin 8s linear infinite reverse" }}>
              <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.8)]" />
            </div>
            <div className="absolute inset-6 rounded-full border border-blue-400/40 -rotate-45" style={{ animation: "atom-spin 6s linear infinite" }}>
              <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-yellow-300 via-orange-500 to-red-600 shadow-[0_0_28px_rgba(251,146,60,0.9)]" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
              <Atom size={12} weight="fill" /> Cosmos Atómico-Humano
            </p>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mt-1">
              Materializar el Átomo
            </h1>
            <p className="text-sm text-gray-300 mt-2 max-w-3xl">
              Una visión de la civilización y la materia · El Sol como Núcleo, los planetas como electrones en órbita.
              Perfecta unidad · Perfecta forma · Dando vida en razón del equilibrio.
            </p>
            {isAdmin && (
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button onClick={openCreate} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-[10px]" data-testid="atom-new-btn">
                  <Plus size={14} className="mr-1" /> Agregar planeta
                </Button>
                <span className="text-[10px] text-gray-500 self-center">🔒 Solo el admin puede editar. Los demás usuarios ven en modo lectura.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AR / 3D interactive scene — click a planet to select */}
      <div className="card-tactical p-3 md:p-4" data-testid="atom-ar-wrap">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
              <Cube size={12} weight="fill" /> Realidad Aumentada · Sistema orbital
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Arrastra para rotar · rueda del ratón para hacer zoom · haz clic en un planeta para {isAdmin ? "editarlo" : "verlo"} en 360°
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setArMode(false)}
              className={`px-2.5 py-1 rounded text-[10px] uppercase font-bold border transition-all ${!arMode ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}
              data-testid="atom-ar-toggle-3d"
            >
              <Cube size={12} className="inline mr-1" /> 3D
            </button>
            <button
              onClick={() => setArMode(true)}
              className={`px-2.5 py-1 rounded text-[10px] uppercase font-bold border transition-all ${arMode ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}
              data-testid="atom-ar-toggle-ar"
            >
              <VideoCamera size={12} className="inline mr-1" /> AR (cámara)
            </button>
          </div>
        </div>
        <AtomARScene
          items={items}
          arMode={arMode}
          height={480}
          onSelect={(it) => { setViewer(it); setViewerMode("3d"); }}
        />
      </div>

      {/* Grid gallery */}
      {loading ? (
        <p className="text-center text-gray-500 py-12" data-testid="atom-loading">Cargando ecosistema…</p>
      ) : items.length === 0 ? (
        <p className="text-center text-gray-500 py-12" data-testid="atom-empty">
          Aún no hay planetas en el ecosistema.{isAdmin ? " Agrega el primero desde el botón de arriba." : ""}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="atom-grid">
          {items.map((it) => (
            <div
              key={it.id}
              className="group relative card-tactical p-0 overflow-hidden border-white/10 hover:border-[#CCFF00]/40 transition-all"
              data-testid={`atom-card-${it.id}`}
            >
              <button
                type="button"
                onClick={() => setViewer(it)}
                className="block w-full aspect-[16/10] bg-[#0A0A0A] overflow-hidden relative cursor-zoom-in"
                data-testid={`atom-view-${it.id}`}
                title="Ampliar"
              >
                <img src={absUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[8px] uppercase font-black bg-black/70 text-[#CCFF00] border border-[#CCFF00]/30 tracking-widest">
                  #{(it.sort ?? 0) + 1} · Planeta
                </span>
              </button>
              <div className="p-4 space-y-1.5">
                <h3 className="font-['Barlow_Condensed'] text-xl font-black uppercase tracking-tight text-white leading-tight">{it.title}</h3>
                {it.description && <p className="text-xs text-gray-400 leading-relaxed line-clamp-4">{it.description}</p>}
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button onClick={() => share(it)} size="sm" variant="outline" className="h-7 border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[9px] uppercase font-bold" data-testid={`atom-share-${it.id}`}>
                    <ShareNetwork size={11} className="mr-1" /> Compartir
                  </Button>
                  {isAdmin && (
                    <>
                      <Button onClick={() => openEdit(it)} size="sm" variant="outline" className="h-7 border-white/15 text-white text-[9px] uppercase font-bold" data-testid={`atom-edit-${it.id}`}>
                        <PencilSimple size={11} className="mr-1" /> Editar
                      </Button>
                      <Button onClick={() => remove(it)} size="sm" variant="outline" className="h-7 border-red-500/30 text-red-400 hover:bg-red-500/10 text-[9px] uppercase font-bold" data-testid={`atom-delete-${it.id}`}>
                        <Trash size={11} className="mr-1" /> Borrar
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fullscreen viewer with 3D/2D toggle + inline edit access */}
      <Dialog open={!!viewer} onOpenChange={(v) => { if (!v) setViewer(null); }}>
        <DialogContent className="bg-[#050510] border-[#CCFF00]/30 max-w-5xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="atom-viewer-modal">
          {viewer && (
            <>
              <div className="flex items-center justify-between p-3 border-b border-white/10 gap-2 flex-wrap">
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewerMode("3d")}
                    className={`px-2.5 py-1 rounded text-[10px] uppercase font-bold border ${viewerMode === "3d" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-gray-300 border-white/10"}`}
                    data-testid="atom-viewer-mode-3d"
                  >
                    <Cube size={12} className="inline mr-1" /> Vista 3D 360°
                  </button>
                  <button
                    onClick={() => setViewerMode("2d")}
                    className={`px-2.5 py-1 rounded text-[10px] uppercase font-bold border ${viewerMode === "2d" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-gray-300 border-white/10"}`}
                    data-testid="atom-viewer-mode-2d"
                  >
                    <Square size={12} className="inline mr-1" /> Vista 2D
                  </button>
                </div>
                <div className="flex gap-2">
                  {isAdmin && (
                    <button
                      onClick={() => { openEdit(viewer); setViewer(null); }}
                      className="px-2.5 py-1 rounded text-[10px] uppercase font-bold border border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10"
                      data-testid="atom-viewer-edit"
                    >
                      <PencilSimple size={12} className="inline mr-1" /> Editar realidad aumentada
                    </button>
                  )}
                  <button onClick={() => setViewer(null)} className="w-8 h-8 rounded-full bg-black/80 border border-white/20 text-white flex items-center justify-center hover:bg-black" data-testid="atom-viewer-close">
                    <X size={14} />
                  </button>
                </div>
              </div>
              {viewerMode === "3d" ? (
                <div className="p-3 bg-black">
                  <AtomPlanet360 imageUrl={absUrl(viewer.image_url)} height={420} />
                </div>
              ) : (
                <div className="bg-black">
                  <img src={absUrl(viewer.image_url)} alt={viewer.title} className="w-full max-h-[65vh] object-contain" />
                </div>
              )}
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-bold">Materializar el Átomo</p>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-black uppercase text-white leading-tight mt-1">{viewer.title}</h3>
                {viewer.description && <p className="text-sm text-gray-300 mt-2">{viewer.description}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin create/edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="bg-[#0A0A0A] border-[#CCFF00]/30 max-w-lg" data-testid="atom-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
              {dialog === "create" ? "Nuevo planeta" : "Editar planeta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="p.ej. Núcleo Atómico-Sol" className="bg-black border-white/15 text-white h-9" data-testid="atom-form-title" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe la órbita, los electrones, las estaciones humanas…" className="bg-black border-white/15 text-white min-h-[100px]" data-testid="atom-form-desc" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Imagen</Label>
              <div className="flex gap-2 items-center">
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL de la imagen o sube una..." className="bg-black border-white/15 text-white h-9 flex-1" data-testid="atom-form-url" />
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] h-9" data-testid="atom-form-upload">
                  <UploadSimple size={14} className="mr-1" /> {uploading ? "Subiendo…" : "Subir"}
                </Button>
              </div>
              {form.image_url && (
                <img src={absUrl(form.image_url)} alt="preview" className="mt-2 max-h-40 rounded border border-white/10 object-contain" />
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Orden (menor primero)</Label>
              <Input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: parseInt(e.target.value || "0", 10) })} className="bg-black border-white/15 text-white h-9 w-32" data-testid="atom-form-sort" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={close} variant="outline" className="border-white/15">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold" data-testid="atom-form-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes atom-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
